import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { authHeaders } from 'utils/authHeaders'
import useUserLocation, { isPreciseFix, requestUserLocation } from 'hooks/useUserLocation'

/**
 * Where address search ranks results from, and who decided.
 *
 * ## Why this exists
 *
 * The browser's fix is not always a measurement. Windows has a "default
 * location" setting (Privacy & security → Location) that it hands out whenever
 * it cannot work the position out for itself, and it reports that made-up
 * point with a *small* accuracy radius, because as far as Windows is concerned
 * the point is known exactly. Chrome passes it straight through. So a fix can
 * be confident, precise-looking, and twenty miles wrong, and nothing in the
 * Geolocation API distinguishes it from a real one.
 *
 * Observed here: a user in Hollister, CA whose browser reported Gilroy, CA.
 * Searching "Ace Hardware" returned stores near Gilroy and stores 150 miles
 * away, but never the one three miles from the user, because the candidate
 * list Google generates is capped and was generated around the wrong town. No
 * amount of re-sorting on our side can surface a result that was never
 * returned.
 *
 * There is no way to detect this and no way to correct it from inside the page.
 * The only real fix is to show the user where we think they are and let them
 * say otherwise. That is what this is.
 *
 * ## Precedence
 *
 *   1. An origin the screen supplied (the map picker passes its camera centre)
 *   2. An origin the user chose here, which persists
 *   3. The browser's fix
 */

export interface OriginPoint {
	lat: number
	lng: number
}

export interface ChosenOrigin extends OriginPoint {
	/** Shown to the user, e.g. "Hollister, CA". */
	label: string
}

export type OriginSource = 'chosen' | 'device'

const STORAGE_KEY = 'mdt.searchOrigin'
/**
 * `localStorage`, not `sessionStorage`, unlike the device fix. A chosen origin
 * is a standing correction to a machine that reports the wrong town, so it has
 * to outlive the tab. The user can always hand it back with "Use my location".
 */
let chosen: ChosenOrigin | null | undefined

const subscribers = new Set<() => void>()

function readChosen(): ChosenOrigin | null {
	if (chosen !== undefined) return chosen
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		const parsed = raw ? JSON.parse(raw) : null
		chosen =
			parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number'
				? { lat: parsed.lat, lng: parsed.lng, label: String(parsed.label || 'a saved place') }
				: null
	} catch {
		chosen = null
	}
	return chosen
}

function writeChosen(value: ChosenOrigin | null) {
	chosen = value
	try {
		if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
		else localStorage.removeItem(STORAGE_KEY)
	} catch {
		// Private browsing. The module-level value still holds for this tab.
	}
	subscribers.forEach(fn => fn())
}

/* ------------------------------------------------------------------ *
 * Naming the device fix
 *
 * "Near you" is useless when the fix is wrong: it is exactly the phrasing that
 * hid this bug. "Near Gilroy, CA" would have diagnosed it in seconds, so the
 * device fix gets reverse geocoded once per tab purely so we can say where we
 * think the user is.
 * ------------------------------------------------------------------ */

const deviceLabels = new Map<string, string>()
const deviceLabelPending = new Set<string>()

const keyOf = (p: OriginPoint) => `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`

/** "Hollister, CA" from whatever the reverse geocoder returned. */
function placeLabel(address: any): string | null {
	if (!address) return null
	const locality = address.city || address.town || address.village || address.county
	const region = address.state
	const label = [locality, region].filter(Boolean).join(', ')
	return label || address.postcode || null
}

async function resolveDeviceLabel(point: OriginPoint): Promise<void> {
	const key = keyOf(point)
	if (deviceLabels.has(key) || deviceLabelPending.has(key)) return
	deviceLabelPending.add(key)
	try {
		const { data } = await axios.get('/api/geocode/reverse', {
			params: { lat: point.lat, lng: point.lng },
			headers: authHeaders(),
		})
		const label = placeLabel(data?.address)
		if (label) {
			deviceLabels.set(key, label)
			subscribers.forEach(fn => fn())
		}
	} catch {
		// A missing label costs nothing but the nicety. The fix still ranks.
	} finally {
		deviceLabelPending.delete(key)
	}
}

export interface UseSearchOriginResult {
	/** The point to rank around, or null when we have nothing. */
	origin: OriginPoint | null
	/** Human-readable place name, once we have one. */
	label: string | null
	source: OriginSource | null
	/** True only for a device fix whose radius is small enough to trust. */
	isPrecise: boolean
	status: 'idle' | 'locating' | 'ready' | 'unavailable'
	/** Pin an origin the user picked. Persists until they hand it back. */
	setOrigin: (origin: ChosenOrigin) => void
	/** Drop the chosen origin and re-ask the browser. */
	useDeviceLocation: () => void
}

export default function useSearchOrigin(): UseSearchOriginResult {
	const { location: device, status, refresh } = useUserLocation()
	const [, forceRender] = useState(0)

	useEffect(() => {
		const subscriber = () => forceRender(n => n + 1)
		subscribers.add(subscriber)
		return () => {
			subscribers.delete(subscriber)
		}
	}, [])

	const chosenOrigin = readChosen()

	useEffect(() => {
		if (!device || chosenOrigin) return
		void resolveDeviceLabel(device)
	}, [device, chosenOrigin])

	const setOrigin = useCallback((origin: ChosenOrigin) => {
		writeChosen(origin)
	}, [])

	const useDeviceLocation = useCallback(() => {
		writeChosen(null)
		void requestUserLocation(true)
		refresh()
	}, [refresh])

	const lat = chosenOrigin ? chosenOrigin.lat : device?.lat
	const lng = chosenOrigin ? chosenOrigin.lng : device?.lng
	const label = chosenOrigin
		? chosenOrigin.label
		: device
			? deviceLabels.get(keyOf(device)) || null
			: null
	const source: OriginSource | null = chosenOrigin ? 'chosen' : device ? 'device' : null

	/**
	 * Memoised on the PRIMITIVES, and this matters more than it looks.
	 *
	 * Consumers put this object in a `useCallback`/`useEffect` dependency list.
	 * Returning a fresh `{lat, lng}` on every render made the search callback
	 * change identity on every render, which re-armed the debounce, which fired
	 * another Places request, which set state, which re-rendered. The visible
	 * symptom was a dropdown that flickered and reordered continuously, and the
	 * invisible one was a Places request every 150ms for as long as the box was
	 * open.
	 */
	const origin = useMemo<OriginPoint | null>(
		() => (typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null),
		[lat, lng],
	)

	return useMemo(
		() => ({
			origin,
			label,
			source,
			// A place the user named is trusted regardless of any radius.
			isPrecise: source === 'chosen' ? true : isPreciseFix(device),
			status: source === 'chosen' ? ('ready' as const) : status,
			setOrigin,
			useDeviceLocation,
		}),
		[origin, label, source, device, status, setOrigin, useDeviceLocation],
	)
}
